import { useEffect, useRef, useCallback } from 'react';
import socket from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { useMessages } from '../context/MessagesContext';
import { useContacts } from '../context/ContactsContext';
import { useNotification } from './useNotification';

export function useSocket() {
  const { isAuthenticated, jwtToken, user } = useAuth();
  const { addMessage, updateMessage, markAsDelivered, setTyping, decryptAllMessages, sendMessage: contextSendMessage } = useMessages();
  const { selectedContact, contacts, updateContactLastMessage } = useContacts();
  const { showNotification } = useNotification();
  const typingTimeoutRef = useRef(null);

  const handleReceiveMessage = useCallback((data) => {
    const isForMe = data.recipientUuid === user.uuid;
    
    if (isForMe) {
      const newMessage = {
        id: data.id,
        senderUuid: data.senderUuid,
        recipientUuid: user.uuid,
        senderToken: data.senderToken,
        recipientToken: data.recipientToken,
        encryptedKey: data.encryptedKey,
        encryptedData: data.encryptedData,
        iv: data.iv,
        sessionId: data.sessionId,
        timestamp: new Date(data.timestamp),
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : null,
        isSelfDestruct: data.isSelfDestruct,
        decrypted: null,
        isOwn: false,
        deliveredAt: null,
        readAt: null
      };
      
      addMessage(newMessage);
      markAsDelivered(data.id);
      
      if (selectedContact && data.senderUuid === selectedContact.contactUuid) {
        // Message is from selected contact, will show in chat
      } else {
        const contact = contacts.find(c => c.contactUuid === data.senderUuid);
        const senderName = contact?.alias || data.senderUuid.substring(0, 8) + '...';
        showNotification(`📩 New message from ${senderName}`, 'info');
      }
    }
  }, [user.uuid, selectedContact, contacts, addMessage, markAsDelivered, showNotification]);

  const handleMessageDelivered = useCallback((data) => {
    updateMessage(data.messageId, { deliveredAt: new Date(data.timestamp) });
  }, [updateMessage]);

  const handleMessageRead = useCallback((data) => {
    updateMessage(data.messageId, { readAt: new Date(data.timestamp) });
  }, [updateMessage]);

  const handleMessageAcknowledged = useCallback((data) => {
    updateMessage(data.tempId, { id: data.messageId, deliveredAt: data.delivered ? new Date(data.timestamp) : null });
  }, [updateMessage]);

  const handleUserTyping = useCallback(({ senderUuid, isTyping }) => {
    if (selectedContact && senderUuid === selectedContact.contactUuid) {
      setTyping(senderUuid, isTyping);
    }
    
    if (isTyping) {
      setTimeout(() => {
        setTyping(senderUuid, false);
      }, 3000);
    }
  }, [selectedContact, setTyping]);

  const handleUnreadMessages = useCallback(({ messages: unreadMessages }) => {
    unreadMessages.forEach(msg => {
      addMessage({
        ...msg,
        timestamp: new Date(msg.timestamp),
        decrypted: null,
        isOwn: msg.senderUuid === user.uuid
      });
    });
    showNotification(`You have ${unreadMessages.length} unread messages`, 'info');
  }, [user.uuid, addMessage, showNotification]);

  const setupSocketListeners = useCallback(() => {
    socket.on('receiveMessage', handleReceiveMessage);
    socket.on('messageDelivered', handleMessageDelivered);
    socket.on('messageRead', handleMessageRead);
    socket.on('messageAcknowledged', handleMessageAcknowledged);
    socket.on('userTyping', handleUserTyping);
    socket.on('unreadMessages', handleUnreadMessages);
    socket.on('socket:connected', () => {
      showNotification('Connected to chat server', 'success');
    });
    socket.on('socket:disconnected', (reason) => {
      showNotification('Disconnected from chat server', 'warning');
    });
    socket.on('socket:error', (err) => {
      showNotification('Connection error: ' + err.message, 'error');
    });
  }, [handleReceiveMessage, handleMessageDelivered, handleMessageRead, handleMessageAcknowledged, handleUserTyping, handleUnreadMessages, showNotification]);

  const connect = useCallback(() => {
    if (isAuthenticated && jwtToken) {
      socket.connect(jwtToken);
      setupSocketListeners();
    }
  }, [isAuthenticated, jwtToken, setupSocketListeners]);

  const disconnect = useCallback(() => {
    socket.disconnect();
  }, []);

  const sendTyping = useCallback((recipientUuid, isTyping) => {
    if (socket.isConnected()) {
      socket.emit('typing', { recipientUuid, isTyping });
    }
  }, []);

  const handleTyping = useCallback(() => {
    if (!selectedContact) return;
    
    sendTyping(selectedContact.contactUuid, true);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(selectedContact.contactUuid, false);
    }, 2000);
  }, [selectedContact, sendTyping]);

  useEffect(() => {
    if (isAuthenticated && jwtToken && !socket.isConnected()) {
      connect();
    }
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [isAuthenticated, jwtToken, connect]);

  return {
    connect,
    disconnect,
    sendTyping,
    handleTyping,
    isConnected: socket.isConnected(),
  };
}